import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import createId from "@/lib/create-id";
import type { ChatEmailSummary } from "@/types/chat";
import type { ComposeEmailPayload, CreateDraftPayload, EmailContact, EmailMessage, EmailParticipant, EmailThread, EmailThreadListItem, EmailThreadScope, ForwardEmailPayload, MessageAttachmentUrls, ReplyEmailPayload, UpdateDraftPayload } from "@/types/email";
import { EmailContactType, EmailDirection, EmailParticipantRole } from "@/types/enums/email";
import type { CursorPage } from "@/types/pagination";
import { composeEmail, createDraft, deleteDraft, forwardEmail, getAttachmentDownloadUrl, getClinicFolderCounts, getEmailHeader, getEmailSignature, getEmailThread, getMessageAttachmentUrls, replyToThread, saveEmailHeader, saveEmailSignature, searchEmailContacts, sendDraft, updateDraft } from "./email";
import type { SaveEmailHeaderPayload, SaveEmailSignaturePayload } from "./email";
import { invalidateOpportunityContactViews } from "@/api/provider/opportunities/opportunity-query-keys";
import { phlebotomyOrderQueryKeys } from "@/api/provider/phlebotomy-order/phlebotomy-orders-query";
import { rollbackChatDetail, rollbackChatInInfiniteList, updateChatDetail, updateChatOptimistically, updateOrAddEmail } from "@/query/comms/chat";
export const emailQueryKeys = {
  all: ["email"] as const,
  // The clinic mailbox renders a thread with the clinic mailbox contact swapped in, so
  // its detail is cached under a scope-suffixed key rather than sharing the provider
  // entry. Calling this without a scope yields the provider key, which is also the
  // prefix of the clinic one — so it doubles as the non-exact key that matches both.
  thread: (threadId: string, scope: EmailThreadScope = "provider") => scope === "provider" ? [...emailQueryKeys.all, "thread", threadId] as const : [...emailQueryKeys.all, "thread", threadId, scope] as const,
  attachmentUrls: (messageId: string) => [...emailQueryKeys.all, "attachment-urls", messageId] as const,
  attachmentDownload: (attachmentId: string) => [...emailQueryKeys.all, "attachment-download", attachmentId] as const,
  signature: [...["email"], "signature"] as const,
  header: [...["email"], "header"] as const,
  contactSearch: (term: string) => [...emailQueryKeys.all, "contact-search", term] as const
};
export const emailQueryOptions = {
  thread: (threadId: string, scope: EmailThreadScope = "provider") => ({
    queryKey: emailQueryKeys.thread(threadId, scope),
    queryFn: () => getEmailThread(threadId, scope),
    select: (data: EmailThread | null) => data ?? undefined,
    enabled: !!threadId
  }),
  attachmentDownload: (attachmentId: string) => ({
    queryKey: emailQueryKeys.attachmentDownload(attachmentId),
    queryFn: async () => {
      const response = await getAttachmentDownloadUrl(attachmentId);
      return response?.signedUrl ?? null;
    },
    staleTime: 10 * 60 * 1000,
    // 10 minutes (URLs expire in 15)
    enabled: !!attachmentId
  }),
  contactSearch: (term: string) => ({
    queryKey: emailQueryKeys.contactSearch(term),
    queryFn: () => searchEmailContacts(term),
    select: (data: EmailContact[] | null) => data ?? []
  }),
  // Clinic-mailbox folder badge counts. Shares the mailbox's cache key so the
  // sidebar nav badge and the folder rail stay in sync and invalidate together.
  clinicFolderCounts: () => ({
    queryKey: ["email", "clinic-mailbox", "counts"] as const,
    queryFn: getClinicFolderCounts
  })
};

/**
 * Hook to fetch bulk attachment URLs for a message.
 * Primes the individual attachment download cache so downloads don't need
 * another API call.
 */
export function useMessageAttachmentUrls(messageId, t0) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let t2;
  if ($[2] !== messageId) {
    t2 = emailQueryKeys.attachmentUrls(messageId);
    $[2] = messageId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== messageId) {
    t3 = async () => {
      const data = await getMessageAttachmentUrls(messageId);
      if (!data) {
        return null;
      }
      for (const [attachmentId, signedUrl] of Object.entries(data.byAttachmentId)) {
        queryClient.setQueryData(emailQueryKeys.attachmentDownload(attachmentId), signedUrl);
      }
      return data;
    };
    $[4] = messageId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = options.enabled !== false && !!messageId;
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      staleTime: 600000,
      refetchInterval: _temp,
      enabled: t4
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return useQuery(t5);
}

/**
 * Derives FROM/TO participants for an optimistic reply from the thread's
 * existing messages. For a clinic reply the FROM is the clinic address
 * (found as FROM on a previous outbound, or TO on the latest inbound)
 * and TO is the original external sender.
 */
function _temp(query) {
  const data_0 = query.state.data as MessageAttachmentUrls | null | undefined;
  if (!data_0?.expiresAt) {
    return false;
  }
  const expiresAt = new Date(data_0.expiresAt).getTime();
  const now = Date.now();
  const timeUntilRefetch = expiresAt - now - 60000;
  if (timeUntilRefetch <= 0) {
    return 1000;
  }
  return timeUntilRefetch;
}
function buildReplyParticipants(thread: EmailThread | undefined): EmailParticipant[] {
  if (!thread?.messages.length) return [];

  // Find the last inbound message — its FROM is the external sender,
  // its TO is the clinic address
  const lastInbound = [...thread.messages].reverse().find(m => m.direction === "INBOUND");
  if (lastInbound) {
    const externalFrom = lastInbound.participants.find(p => p.role === "FROM");
    const clinicTo = lastInbound.participants.find(p => p.role === "TO");
    const participants: EmailParticipant[] = [];
    if (clinicTo) participants.push({
      ...clinicTo,
      role: EmailParticipantRole.FROM
    });
    if (externalFrom) participants.push({
      ...externalFrom,
      role: EmailParticipantRole.TO
    });
    return participants;
  }

  // Fallback: copy FROM from the last outbound message
  const lastOutbound = [...thread.messages].reverse().find(m => m.direction === "OUTBOUND");
  if (lastOutbound) {
    return lastOutbound.participants.filter(p => p.role === "FROM" || p.role === "TO");
  }
  return [];
}

/**
 * Predicates for the mailbox's refinement filters, keyed by the EmailFilterChip
 * values (see comms/email/types.ts). The clinic-mailbox list key is
 * ["email","clinic-mailbox", folder, search, activeFilters, sentBy], so a cached list's chips
 * tell us whether a freshly-patched row still belongs in it. An unrecognized chip
 * counts as matching, keeping the old patch-in-place behavior.
 */
const CLINIC_MAILBOX_FILTER_PREDICATES: Record<string, (item: EmailThreadListItem) => boolean> = {
  unread: item => !item.isRead,
  starred: item => item.isStarred,
  "has-attachment": item => item.hasAttachments,
  patients: item => item.primaryPatientId !== null
};
function rowMatchesListFilters(item: EmailThreadListItem, activeFilters: readonly unknown[]): boolean {
  return activeFilters.every(chip => typeof chip === "string" ? CLINIC_MAILBOX_FILTER_PREDICATES[chip]?.(item) ?? true : true);
}

/**
 * Patch a clinic-mailbox thread-list row from a server-returned thread list-item — the
 * response-driven alternative to invalidating + refetching (mirrors patchPatientDocList /
 * fax's list patching). The cache holds an InfiniteData of CursorPage<EmailThreadListItem>.
 *
 * For a row already in a list: it's replaced where it still belongs and dropped where a
 * chip no longer matches (a reply that read the thread falls out of an Unread list),
 * instead of lingering as a row that contradicts the filter.
 *
 * `insertFolders` names folders whose membership the action *gains* — a reply gives an
 * inbound-only thread its first sent message, so it now belongs in Sent (and All). The
 * returned row is inserted at the top of those cached lists when absent (a reply is the
 * thread's newest message, so it sorts to the top by last_message_date desc), which keeps
 * the just-sent reply from hiding until an unrelated refetch. Insertion is limited to
 * unfiltered, unsearched lists: a chip- or search-scoped (or patient-grouped) list has an
 * ordering we can't place a single row into correctly, so those refetch instead. A list
 * narrowed to one sender also takes the row when the reply is theirs (the row's sentBy is
 * the newest send — this reply); any other sender-filtered list missing the row is marked
 * stale, since the reply may have added it there too. A row already in a sender-filtered
 * list always stays: the thread still contains that sender's email.
 *
 * Lists are visited through the query cache rather than setQueriesData because the updater
 * needs each list's key to read its folder/chips. The ["email","clinic-mailbox"] prefix
 * also matches the folder-counts query (a different shape), so skip anything without
 * `pages`.
 */
function patchClinicMailboxRow(item: EmailThreadListItem, {
  insertFolders = []
}: {
  insertFolders?: string[];
} = {}): void {
  const cachedLists = queryClient.getQueryCache().findAll({
    queryKey: ["email", "clinic-mailbox"]
  });
  for (const {
    queryKey
  } of cachedLists) {
    // ["email","clinic-mailbox", folder, search, activeFilters, sentBy]
    const folder = queryKey[2];
    const search = queryKey[3];
    const activeFilters = queryKey[4];
    const sentBy = queryKey[5];
    const matchesFilters = !Array.isArray(activeFilters) || rowMatchesListFilters(item, activeFilters);
    const canInsert = typeof folder === "string" && insertFolders.includes(folder) && (search === undefined || search === "") && Array.isArray(activeFilters) && activeFilters.length === 0 && (sentBy == null || sentBy === item.sentBy?.id);

    // A reply can give a thread its first send by someone, adding it to that
    // person's sender-filtered list in any folder (Inbox, Starred, …). Which folders
    // it now matches, and where it sorts among a filtered list's rows, can't be told
    // client-side, so a sender-filtered list that lacks the row is marked stale —
    // refetched now if it's on screen, otherwise the next time it's shown.
    if (insertFolders.length > 0 && sentBy != null && !canInsert) {
      const cached = queryClient.getQueryData<InfiniteData<CursorPage<EmailThreadListItem> | null>>(queryKey);
      const present = cached?.pages?.some(page => page?.data.some(thread => thread.id === item.id));
      if (cached?.pages && !present) {
        void queryClient.invalidateQueries({
          queryKey,
          exact: true
        });
        continue;
      }
    }
    queryClient.setQueryData<InfiniteData<CursorPage<EmailThreadListItem> | null>>(queryKey, old => {
      if (!old?.pages) return old;
      const present = old.pages.some(page => page?.data.some(thread => thread.id === item.id));
      if (!present) {
        if (!canInsert) return old;
        // Prepend to the first page — the reply is the newest message in this folder.
        return {
          ...old,
          pages: old.pages.map((page, index) => page && index === 0 ? {
            ...page,
            data: [item, ...page.data]
          } : page)
        };
      }
      return {
        ...old,
        pages: old.pages.map(page => page ? {
          ...page,
          data: matchesFilters ? page.data.map(thread => thread.id === item.id ? item : thread) : page.data.filter(thread => thread.id !== item.id)
        } : page)
      };
    });
  }
}

/** Context captured by useReplyToThread.onMutate, replayed to undo the optimistic reply. */
interface ReplyRollbackContext {
  previousThread: EmailThread | undefined;
  previousDetail: Parameters<typeof rollbackChatDetail>[1] | undefined;
  previousInfiniteListData: Parameters<typeof rollbackChatInInfiniteList>[0] | undefined;
}

/**
 * Undo the optimistic SENDING reply in the thread cache and, for a chat-window reply, in
 * the chat caches. Shared by onError and by the two failure shapes that reach onSuccess.
 */
function rollbackOptimisticReply(threadId: string, scope: EmailThreadScope, chatId: string | undefined, context: ReplyRollbackContext): void {
  if (context.previousThread) {
    queryClient.setQueryData<EmailThread>(emailQueryKeys.thread(threadId, scope), context.previousThread);
  }
  if (!chatId || !context.previousInfiniteListData) return;
  rollbackChatDetail(chatId, context.previousDetail);
  rollbackChatInInfiniteList(context.previousInfiniteListData);
}

/**
 * Mark a thread read in *every* cached clinic-mailbox list, not just the one on screen —
 * otherwise switching folders serves a stale cache that still shows the thread unread for
 * the rest of the stale window.
 *
 * The list the user is looking at keeps the row even when it's filtered to Unread: they're
 * reading that thread right now, and pulling the row out from under the open reader is
 * worse than briefly showing a read row in an Unread view. Every other cached Unread list
 * drops it, since a read thread no longer belongs there.
 */
export function markClinicMailboxRowRead(client: QueryClient, threadId: string, activeKey: readonly unknown[]): void {
  const activeKeyId = JSON.stringify(activeKey);
  const cachedLists = client.getQueryCache().findAll({
    queryKey: ["email", "clinic-mailbox"]
  });
  for (const {
    queryKey
  } of cachedLists) {
    // ["email","clinic-mailbox", folder, search, activeFilters, sentBy]
    const activeFilters = queryKey[4];
    // isRead is the only row field the chips key on that this changes, so Unread is the
    // only filter a newly-read row can fall out of.
    const keepRow = JSON.stringify(queryKey) === activeKeyId || !Array.isArray(activeFilters) || !activeFilters.includes("unread");
    client.setQueryData<InfiniteData<CursorPage<EmailThreadListItem> | null>>(queryKey, old => {
      if (!old?.pages) return old;
      return {
        ...old,
        pages: old.pages.map(page => page ? {
          ...page,
          data: keepRow ? page.data.map(thread => thread.id === threadId ? {
            ...thread,
            isRead: true
          } : thread) : page.data.filter(thread => thread.id !== threadId)
        } : page)
      };
    });
  }
}
export function useReplyToThread() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp2,
      onMutate: _temp5,
      onSuccess: _temp6,
      onError: _temp7
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp7(_error, t0, context_0) {
  const {
    threadId: threadId_2,
    chatId: chatId_1,
    scope: t1
  } = t0;
  const scope_1 = t1 === undefined ? "provider" : t1;
  if (!context_0) {
    return;
  }
  rollbackOptimisticReply(threadId_2, scope_1, chatId_1, context_0);
}
function _temp6(response, t0, context) {
  const {
    threadId: threadId_1,
    chatId: chatId_0,
    scope: t1
  } = t0;
  const scope_0 = t1 === undefined ? "provider" : t1;
  if (!context) {
    return;
  }
  const {
    tempId: tempId_0
  } = context;
  if (!response || response.success === false) {
    rollbackOptimisticReply(threadId_1, scope_0, chatId_0, context);
    return;
  }
  const realId = response.messageId;
  if (realId && chatId_0) {
    updateChatDetail(chatId_0, chat_1 => {
      const webhookAlreadyArrived = chat_1.emails.some(e => e.id === realId);
      if (webhookAlreadyArrived) {
        return {
          ...chat_1,
          emails: chat_1.emails.filter(e_0 => e_0.id !== tempId_0)
        };
      }
      return {
        ...chat_1,
        emails: chat_1.emails.map(e_1 => e_1.id === tempId_0 ? {
          ...e_1,
          id: realId,
          status: "SENT"
        } : e_1)
      };
    });
  }
  const serverMessage = response.message;
  if (serverMessage) {
    queryClient.setQueryData(emailQueryKeys.thread(threadId_1, scope_0), old => {
      if (!old) {
        return old;
      }
      const hasTemp = old.messages.some(m => m.id === tempId_0);
      return {
        ...old,
        messages: hasTemp ? old.messages.map(m_0 => m_0.id === tempId_0 ? serverMessage : m_0) : [...old.messages, serverMessage],
        sentBy: serverMessage.sentBy
      };
    });
  }
  if (scope_0 === "clinic" && response.threadListItem) {
    patchClinicMailboxRow(response.threadListItem, {
      insertFolders: ["sent", "all"]
    });
  }
}
async function _temp5(t0) {
  const {
    threadId: threadId_0,
    chatId,
    scope: t1,
    payload: payload_0
  } = t0;
  const scope = t1 === undefined ? "provider" : t1;
  const tempId = createId("eml");
  const threadKey = emailQueryKeys.thread(threadId_0, scope);
  await queryClient.cancelQueries({
    queryKey: threadKey
  });
  const previousThread = queryClient.getQueryData(threadKey);
  const subject = previousThread?.subject ?? null;
  const derivedParticipants = buildReplyParticipants(previousThread);
  const replyParticipants = payload_0.toOverride?.length ? [...derivedParticipants.filter(_temp3), ...payload_0.toOverride.map(_temp4)] : derivedParticipants;
  const now = new Date().toISOString();
  const optimisticMessage = {
    id: tempId,
    threadId: threadId_0,
    subject,
    bodyText: payload_0.bodyText ?? null,
    bodyHtml: payload_0.bodyHtml ?? null,
    snippet: payload_0.bodyText?.slice(0, 100) ?? null,
    status: "SENDING",
    direction: EmailDirection.OUTBOUND,
    messageType: "CUSTOM",
    sentDate: null,
    deliveredDate: null,
    isRead: true,
    isStarred: false,
    isDraft: false,
    draftKind: null,
    parentMessageId: null,
    sentBy: null,
    participants: replyParticipants,
    attachments: [],
    openCount: 0,
    uniqueOpenCount: 0,
    clickCount: 0,
    uniqueClickCount: 0,
    firstOpenedDate: null,
    lastOpenedDate: null,
    createdDate: now,
    updatedDate: null
  };
  if (previousThread) {
    queryClient.setQueryData(threadKey, {
      ...previousThread,
      messages: [...previousThread.messages, optimisticMessage]
    });
  }
  if (!chatId) {
    return {
      tempId,
      previousThread,
      previousDetail: undefined,
      previousInfiniteListData: undefined,
      chatId,
      scope
    };
  }
  const optimisticEmailSummary = {
    id: tempId,
    threadId: threadId_0,
    subject,
    snippet: payload_0.bodyText?.slice(0, 100) ?? null,
    direction: EmailDirection.OUTBOUND,
    status: "SENDING",
    sentDate: null,
    createdDate: new Date().toISOString()
  };
  const {
    previousDetail,
    previousInfiniteListData
  } = updateChatOptimistically(chatId, chat => ({
    ...chat,
    emails: updateOrAddEmail(chat.emails, optimisticEmailSummary)
  }), chat_0 => ({
    ...chat_0,
    latestMessageContent: subject ?? "Email reply",
    latestMessageDate: optimisticEmailSummary.createdDate
  }));
  return {
    tempId,
    previousThread,
    previousDetail,
    previousInfiniteListData,
    chatId,
    scope
  };
}
function _temp4(email) {
  return {
    id: createId("ecn"),
    role: EmailParticipantRole.TO,
    contact: {
      id: createId("ecn"),
      email,
      displayName: null,
      contactType: EmailContactType.EXTERNAL,
      patientIds: [],
      patients: []
    }
  };
}
function _temp3(p) {
  return p.role === "FROM";
}
function _temp2(t0) {
  const {
    threadId,
    payload,
    files
  } = t0;
  return replyToThread(threadId, payload, files);
}
export function useComposeEmail() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp8,
      onSuccess: _temp9
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp9(result, variables) {
  if (result?.success && variables.payload.patientId) {
    queryClient.invalidateQueries({
      queryKey: phlebotomyOrderQueryKeys.list(variables.payload.patientId)
    });
    invalidateOpportunityContactViews();
  }
}
function _temp8(t0) {
  const {
    payload,
    files,
    documentIds
  } = t0;
  return composeEmail(payload, files, documentIds);
}
export function useForwardEmail() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp10,
      onSuccess: _temp11
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp11(_data, variables) {
  queryClient.invalidateQueries({
    queryKey: emailQueryKeys.thread(variables.threadId)
  });
  queryClient.invalidateQueries({
    queryKey: ["email", "clinic-mailbox"]
  });
}
function _temp10(t0) {
  const {
    threadId,
    payload,
    files
  } = t0;
  return forwardEmail(threadId, payload, files);
}
export function useSaveDraft() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp12,
      onSuccess: _temp15
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp15(data, variables_0) {
  if (!data) {
    return;
  }
  if (variables_0.draftId) {
    if (data.threadId) {
      queryClient.setQueriesData({
        queryKey: emailQueryKeys.thread(data.threadId)
      }, old => old ? {
        ...old,
        messages: old.messages.map(message => message.id === data.id ? {
          ...message,
          ...data,
          parentMessageId: message.parentMessageId
        } : message)
      } : old);
    }
    if (data.draftKind && data.draftKind !== "NEW") {
      return;
    }
    const nextToContacts = data.participants.filter(_temp13).map(_temp14);
    const nextHasAttachments = data.attachments.length > 0;
    queryClient.setQueriesData({
      queryKey: ["email", "clinic-mailbox"]
    }, old_0 => {
      if (!old_0?.pages) {
        return old_0;
      }
      return {
        ...old_0,
        pages: old_0.pages.map(page => page ? {
          ...page,
          data: page.data.map(thread => thread.id === data.threadId ? {
            ...thread,
            subject: data.subject,
            snippet: data.snippet,
            toContacts: nextToContacts,
            hasAttachments: nextHasAttachments
          } : thread)
        } : page)
      };
    });
    return;
  }
  queryClient.invalidateQueries({
    queryKey: ["email", "clinic-mailbox"]
  });
  if (data.threadId) {
    queryClient.invalidateQueries({
      queryKey: emailQueryKeys.thread(data.threadId)
    });
  }
}
function _temp14(participant_0) {
  return participant_0.contact;
}
function _temp13(participant) {
  return participant.role === "TO";
}
function _temp12(variables) {
  if (variables.draftId) {
    return updateDraft(variables.draftId, variables.payload);
  }
  return createDraft(variables.payload);
}
export function useDeleteDraft() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp16,
      onSuccess: _temp17
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp17() {
  queryClient.invalidateQueries({
    queryKey: ["email", "clinic-mailbox"]
  });
  queryClient.invalidateQueries({
    queryKey: [...emailQueryKeys.all, "thread"]
  });
}
function _temp16(draftId) {
  return deleteDraft(draftId);
}
export function useSendDraft() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp18,
      onSuccess: _temp19
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp19() {
  queryClient.invalidateQueries({
    queryKey: emailQueryKeys.all
  });
}
function _temp18(draftId) {
  return sendDraft(draftId);
}
export function useEmailSignature() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: emailQueryKeys.signature,
      queryFn: getEmailSignature
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}
export function useSaveEmailSignature() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp20,
      onSuccess: _temp21
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp21() {
  return queryClient.invalidateQueries({
    queryKey: emailQueryKeys.signature
  });
}
function _temp20(payload) {
  return saveEmailSignature(payload);
}
export function useEmailHeader(options) {
  const $ = _c(2);
  const t0 = options?.enabled ?? true;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      queryKey: emailQueryKeys.header,
      queryFn: getEmailHeader,
      enabled: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return useQuery(t1);
}
export function useSaveEmailHeader() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp22,
      onSuccess: _temp23
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp23() {
  return queryClient.invalidateQueries({
    queryKey: emailQueryKeys.header
  });
}
function _temp22(payload) {
  return saveEmailHeader(payload);
}